import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, Check } from "lucide-react";
import { bookCall } from "../lib/api";

const empty = { name: "", email: "", company: "", role: "", message: "" };

export const LeadModal = ({ open, onClose, title, subtitle, topic, onSuccess }) => {
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await bookCall({ ...form, topic });
      onSuccess?.();
      setStatus("done");
      setForm(empty);
    } catch {
      setStatus("error");
    }
  };

  const close = () => { setStatus("idle"); onClose(); };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="lead-modal"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={close}
        >
          <motion.div
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto p-8 sm:p-10 relative"
          >
            <button data-testid="lead-close" onClick={close} className="absolute top-5 right-5 text-[var(--ink-soft)] hover:text-[var(--petrol)]"><X strokeWidth={1.5} /></button>

            {status === "done" ? (
              <div className="py-10 text-center" data-testid="lead-success">
                <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[var(--petrol)] flex items-center justify-center text-white"><Check strokeWidth={2} /></div>
                <h3 className="font-serif text-3xl text-[var(--ink)] mb-2">Richiesta inviata</h3>
                <p className="text-[var(--ink-soft)]">Ti ricontatteremo con i dettagli della prossima edizione.</p>
                <button onClick={close} className="btn-fill mt-8 text-xs uppercase tracking-[0.15em] font-semibold border border-[var(--petrol)] text-[var(--petrol)] hover:text-white px-8 py-3">Chiudi</button>
              </div>
            ) : (
              <>
                <div className="overline mb-3">{title}</div>
                <h3 className="font-serif text-2xl text-[var(--ink)] mb-2">{subtitle}</h3>
                <p className="text-sm text-[var(--ink-soft)] mb-6">{topic}</p>
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Field testid="lead-name" placeholder="Nome*" value={form.name} onChange={set("name")} required />
                    <Field testid="lead-email" type="email" placeholder="Email*" value={form.email} onChange={set("email")} required />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field testid="lead-company" placeholder="Azienda" value={form.company} onChange={set("company")} />
                    <Field testid="lead-role" placeholder="Ruolo" value={form.role} onChange={set("role")} />
                  </div>
                  <textarea data-testid="lead-message" placeholder="Note (facoltative)" value={form.message} onChange={set("message")} rows={3}
                    className="w-full border border-[var(--line)] px-4 py-3 text-sm focus:border-[var(--petrol)] outline-none resize-none" />
                  {status === "error" && <p className="text-sm text-red-600" data-testid="lead-error">Invio non riuscito. Riprova.</p>}
                  <button data-testid="lead-submit" disabled={status === "loading"}
                    className="btn-fill w-full inline-flex justify-center items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold bg-[var(--petrol)] text-white px-8 py-4 disabled:opacity-60">
                    {status === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Invia richiesta"}
                  </button>
                  <p className="text-xs text-[var(--ink-soft)]">Inviando accetti di essere ricontattato/a. Nessuno spam.</p>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Field = ({ testid, ...props }) => (
  <input data-testid={testid} {...props}
    className="w-full border border-[var(--line)] px-4 py-3 text-sm focus:border-[var(--petrol)] outline-none" />
);
